"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { UserPlus, Loader2, CheckCircle, ChevronDown, Eye, EyeOff } from "lucide-react";
import CountrySelectModal, { COUNTRIES_STATIC, useCountry } from "@/components/CountrySelectModal";

const API = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

function PasswordStrength({ password }: { password: string }) {
    const hasLength = password.length >= 8;
    const hasLetter = /[A-Za-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const score = [hasLength, hasLetter, hasDigit, hasSpecial].filter(Boolean).length;

    if (!password) return null;

    const colors = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-green-500"];
    const labels = ["ضعيفة جداً", "ضعيفة", "متوسطة", "قوية"];

    return (
        <div className="mt-2">
            <div className="flex gap-1 mb-1">
                {[0, 1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-all ${i < score ? colors[score - 1] : "bg-white/20"}`}
                    />
                ))}
            </div>
            <p className={`text-xs ${score < 2 ? "text-red-400" : score < 4 ? "text-yellow-400" : "text-green-400"}`}>
                قوة كلمة المرور: {labels[score - 1] || "أدخل كلمة مرور"}
            </p>
        </div>
    );
}

export default function PublicRegisterPage() {
    const router = useRouter();
    const { setCountry } = useCountry();

    const [form, setForm] = useState({
        full_name: "",
        phone: "",
        email: "",
        password: "",
        confirm_password: "",
        city: "",
    });
    const [selectedCountry, setSelectedCountry] = useState(COUNTRIES_STATIC[0]);
    const [countryModalOpen, setCountryModalOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [done, setDone] = useState(false);

    const set = (key: string, value: string) => {
        setForm((p) => ({ ...p, [key]: value }));
        setFieldErrors((p) => ({ ...p, [key]: "" }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setFieldErrors({});

        // Client-side confirmation check
        if (form.password !== form.confirm_password) {
            setFieldErrors({ confirm_password: "كلمتا المرور غير متطابقتين." });
            setLoading(false);
            return;
        }

        try {
            const res = await fetch(`${API}/api/auth/public-register/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    full_name: form.full_name,
                    phone: `${selectedCountry.phone_prefix}${form.phone}`,
                    email: form.email || undefined,
                    password: form.password,
                    confirm_password: form.confirm_password,
                    country_code: selectedCountry.code,
                    city: form.city,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                // Map field errors
                const fe: Record<string, string> = {};
                if (data.phone) fe.phone = Array.isArray(data.phone) ? data.phone[0] : data.phone;
                if (data.email) fe.email = Array.isArray(data.email) ? data.email[0] : data.email;
                if (data.password) fe.password = Array.isArray(data.password) ? data.password[0] : data.password;
                if (data.confirm_password) fe.confirm_password = Array.isArray(data.confirm_password) ? data.confirm_password[0] : data.confirm_password;
                if (Object.keys(fe).length) {
                    setFieldErrors(fe);
                } else {
                    setError(data.non_field_errors?.[0] || data.detail || Object.values(data)[0]?.[0] || "حدث خطأ. حاول مجدداً.");
                }
                return;
            }

            // Persist tokens + country
            localStorage.setItem("s360_access", data.access);
            localStorage.setItem("s360_refresh", data.refresh);
            if (data.user) localStorage.setItem("s360_user", JSON.stringify(data.user));
            setCountry(selectedCountry);
            setDone(true);
            setTimeout(() => router.push("/customer-dashboard"), 1800);
        } catch {
            setError("خطأ في الاتصال بالخادم.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 flex items-center justify-center p-4">
                <div className="text-center">
                    <CheckCircle className="h-24 w-24 text-green-400 mx-auto mb-6 animate-bounce" />
                    <h2 className="text-2xl font-black text-white mb-2">مرحباً {form.full_name}!</h2>
                    <p className="text-orange-300">تم إنشاء حسابك بنجاح. يتم توجيهك...</p>
                </div>
            </div>
        );
    }

    const Field = ({ label, children, error: ferr }: { label: string; children: React.ReactNode; error?: string }) => (
        <div>
            <label className="block text-sm font-medium text-orange-200 mb-1.5">{label}</label>
            {children}
            {ferr && <p className="text-red-400 text-xs mt-1">{ferr}</p>}
        </div>
    );

    const inputClass = "w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition";

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 flex items-center justify-center p-4">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/3 -left-24 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/3 -right-24 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
            </div>

            <div className="relative w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-500 rounded-3xl shadow-2xl shadow-orange-900/50 mb-4">
                        <UserPlus className="h-10 w-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-black text-white">إنشاء حساب عميل</h1>
                    <p className="text-orange-300 text-sm mt-1">انضم إلى منصة سيارتي 360</p>
                </div>

                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                    <form onSubmit={handleSubmit} className="space-y-4">

                        {/* Full Name */}
                        <Field label="الاسم الكامل *">
                            <input
                                type="text" value={form.full_name} onChange={e => set("full_name", e.target.value)} required
                                className={inputClass} placeholder="مثال: محمد علي"
                            />
                        </Field>

                        {/* Phone */}
                        <Field label="رقم الجوال *" error={fieldErrors.phone}>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setCountryModalOpen(true)}
                                    className="bg-white/10 border border-white/20 text-white rounded-2xl px-3 py-3 text-sm flex items-center gap-1 min-w-[90px] hover:bg-white/15 transition shrink-0"
                                >
                                    <span className="text-lg">{selectedCountry.emoji}</span>
                                    <span className="text-xs">{selectedCountry.phone_prefix}</span>
                                    <ChevronDown className="h-3 w-3 text-white/40" />
                                </button>
                                <input
                                    type="tel" value={form.phone} onChange={e => set("phone", e.target.value)} required dir="ltr"
                                    className="flex-1 bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
                                    placeholder="555 123 456"
                                />
                            </div>
                        </Field>

                        {/* Country */}
                        <Field label="الدولة *">
                            <button
                                type="button"
                                onClick={() => setCountryModalOpen(true)}
                                className="w-full bg-white/10 border border-white/20 text-white rounded-2xl px-4 py-3 text-sm flex items-center justify-between hover:bg-white/15 transition"
                            >
                                <span className="flex items-center gap-2">
                                    <span className="text-xl">{selectedCountry.emoji}</span>
                                    <span>{selectedCountry.name_ar}</span>
                                </span>
                                <ChevronDown className="h-4 w-4 text-white/50" />
                            </button>
                        </Field>

                        {/* City */}
                        <Field label="المدينة (اختياري)">
                            <input
                                type="text" value={form.city} onChange={e => set("city", e.target.value)}
                                className={inputClass} placeholder="مثال: الرياض"
                            />
                        </Field>

                        {/* Email */}
                        <Field label="البريد الإلكتروني (اختياري)" error={fieldErrors.email}>
                            <input
                                type="email" value={form.email} onChange={e => set("email", e.target.value)} dir="ltr"
                                className={inputClass} placeholder="example@email.com"
                            />
                        </Field>

                        {/* Password */}
                        <Field label="كلمة المرور *" error={fieldErrors.password}>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"} value={form.password}
                                    onChange={e => set("password", e.target.value)} required dir="ltr"
                                    className={`${inputClass} pr-12`} placeholder="••••••••"
                                />
                                <button type="button" onClick={() => setShowPassword(v => !v)}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition">
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                            <PasswordStrength password={form.password} />
                        </Field>

                        {/* Confirm Password */}
                        <Field label="تأكيد كلمة المرور *" error={fieldErrors.confirm_password}>
                            <div className="relative">
                                <input
                                    type={showConfirm ? "text" : "password"} value={form.confirm_password}
                                    onChange={e => set("confirm_password", e.target.value)} required dir="ltr"
                                    className={`${inputClass} pr-12`} placeholder="••••••••"
                                />
                                <button type="button" onClick={() => setShowConfirm(v => !v)}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition">
                                    {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                        </Field>

                        {error && (
                            <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-3 text-red-300 text-sm text-center">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit" disabled={loading}
                            className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-800 disabled:cursor-not-allowed text-white font-bold rounded-2xl py-3.5 text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-orange-900/50"
                        >
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                            {loading ? "جاري إنشاء الحساب..." : "إنشاء الحساب"}
                        </button>
                    </form>

                    <p className="text-center text-white/40 text-xs mt-4">
                        لديك حساب بالفعل؟{" "}
                        <Link href="/login" className="text-blue-400 hover:underline">تسجيل الدخول</Link>
                        {" · "}
                        <Link href="/register" className="text-orange-400 hover:underline">سجّل كتاجر</Link>
                    </p>
                </div>
            </div>

            <CountrySelectModal
                isOpen={countryModalOpen}
                onClose={() => setCountryModalOpen(false)}
                onSelect={(c) => setSelectedCountry(c)}
            />
        </div>
    );
}
